import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Check, Copy, KeyRound, RotateCcw, ShieldOff } from 'lucide-react';

// Credenziali Kafka della console Sangfor EPP del tenant (backend routes/eppKafka.ts, 2026-11-03):
// il ricevitore `epp-kafka` riceve l'inventario "Asset Info". Solo platform admin. La password si
// vede una volta sola, nella risposta di creazione o rotazione: non è salvata da nessuna parte.

interface Status {
  configured: boolean; state: 'unknown' | 'none' | 'active' | 'revoked' | 'incomplete'; server: string | null;
  consoleRegistered: boolean; lastProcessedAt: string | null;
  username?: string; assetsTopic?: string; logsTopic?: string; messages?: { assets: number | null; logs: number | null };
}
interface Issued { username: string; password: string; assetsTopic: string; logsTopic: string; server: string | null }

function Field({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-xs text-gray-500 py-0.5">{label}</dt>
      <dd className="text-sm py-0.5 font-mono break-all">{value}</dd>
    </>
  );
}

export default function EppKafkaCredentials({ tenantId }: { tenantId: number }) {
  const [status, setStatus] = useState<Status | null>(null);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const passwordRef = useRef<HTMLElement>(null);

  function load() {
    setError('');
    axios.get(`/api/epp-kafka/${tenantId}`).then(res => setStatus(res.data.data))
      .catch(err => { setStatus(null); setError(err.response?.data?.error || 'Could not read the Kafka receiver'); });
  }

  useEffect(() => { setIssued(null); load(); }, [tenantId]);

  async function run(action: () => Promise<{ data: { data?: Issued } }>) {
    setBusy(true); setError(''); setCopied(false);
    try {
      const res = await action();
      if (res.data.data?.password) setIssued(res.data.data);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'The operation did not complete');
      load();
    } finally { setBusy(false); }
  }

  const create = () => run(() => axios.post(`/api/epp-kafka/${tenantId}/credentials`));
  const rotate = () => {
    if (window.confirm('Create a new password? New logins with the old one are refused right away: the console stops sending as soon as it reconnects, until you enter the new password there.')) {
      run(() => axios.post(`/api/epp-kafka/${tenantId}/credentials/rotate`));
    }
  };
  const revoke = () => {
    if (window.confirm('Revoke these credentials? The console can no longer send, messages still in the topic are ignored and the Sangfor EPP inventory of this tenant is switched off. The topics stay and expire by themselves.')) {
      run(() => axios.delete(`/api/epp-kafka/${tenantId}/credentials`));
    }
  };

  // navigator.clipboard può essere negato dal browser: si ripiega sulla selezione del testo.
  async function copy() {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.password);
      setCopied(true);
    } catch {
      const node = passwordRef.current;
      const selection = window.getSelection();
      if (node && selection) { selection.selectAllChildren(node); }
      if (node && selection && document.execCommand('copy')) { selection.removeAllRanges(); setCopied(true); }
      else setError('Copy not allowed by the browser: the password is selected, press Ctrl+C');
    }
  }

  const server = issued?.server || status?.server || '—';
  const active = status?.state === 'active';
  return (
    <div>
      <h3 className="text-sm font-medium text-gray-700 mb-1 inline-flex items-center gap-1.5"><KeyRound size={14} className="text-gray-400" /> Kafka reporting credentials</h3>
      <p className="text-xs text-gray-500 mb-2">
        What to enter in the console under Data Sync → Kafka, to receive the asset inventory. The account can only write to these topics.
        The console sends with SASL/PLAIN without TLS, so the password travels unencrypted on the LAN.
      </p>

      {error && <p role="alert" className="text-sm text-red-700 mb-2">{error}</p>}

      {status && !status.configured && (
        <p className="text-sm text-gray-500">The portal is not connected to the Kafka receiver yet: credentials cannot be managed from here on this server.</p>
      )}

      {issued && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 mb-3 space-y-2">
          <p className="text-sm font-medium text-amber-900 inline-flex items-center gap-1.5"><AlertTriangle size={14} /> Copy the password of {issued.username} now: it will not be shown again</p>
          <div className="flex flex-wrap items-center gap-2">
            <code ref={passwordRef} className="text-sm bg-white border border-amber-200 rounded px-2 py-1 break-all select-all">{issued.password}</code>
            <button onClick={copy} className="btn-secondary text-xs inline-flex items-center gap-1">{copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}</button>
            <button onClick={() => setIssued(null)} className="text-xs text-gray-600 hover:underline">I have saved it</button>
          </div>
        </div>
      )}

      {status?.configured && (
        <>
          {!status.consoleRegistered && (
            <p className="text-sm text-amber-800 mb-2 inline-flex items-start gap-1.5"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> No dedicated console is registered for this tenant: its messages would be discarded. Register it above once it appears in the logs.</p>
          )}

          {status.username && status.state !== 'revoked' && (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 mb-2 max-w-xl">
              <Field label="Server" value={server} />
              <Field label="Authentication" value="SASL/PLAIN, no TLS" />
              <Field label="Username" value={status.username} />
              <Field label="Asset Info topic" value={status.assetsTopic || '—'} />
              <Field label="Agent Log topic" value={status.logsTopic || '—'} />
            </dl>
          )}

          {active && (
            <p className="text-xs text-gray-500 mb-2">
              Asset messages received: {status.messages?.assets ?? '—'}
              {status.lastProcessedAt ? ` · last processed ${new Date(status.lastProcessedAt).toLocaleString()}` : ' · nothing processed yet'}
              . Agent Log messages are stored but not read by the platform.
            </p>
          )}
          {status.state === 'revoked' && <p className="text-sm text-gray-500 mb-2">Credentials revoked. New credentials use a new username on the same topics and switch the inventory back on.</p>}
          {status.state === 'incomplete' && <p className="text-sm text-amber-800 mb-2">A previous attempt did not finish: no account exists yet. Create the credentials again.</p>}

          <div className="flex flex-wrap gap-2">
            {!active && (status.state === 'none' || status.state === 'revoked' || status.state === 'incomplete') && (
              <button onClick={create} disabled={busy} className="btn-primary text-sm inline-flex items-center gap-1"><KeyRound size={13} /> {busy ? 'Working…' : 'Create credentials'}</button>
            )}
            {status.state === 'incomplete' && <button onClick={revoke} disabled={busy} className="text-sm text-red-700 hover:underline inline-flex items-center gap-1 px-2"><ShieldOff size={13} /> Revoke</button>}
            {active && <>
              <button onClick={rotate} disabled={busy} className="btn-secondary text-sm inline-flex items-center gap-1"><RotateCcw size={13} /> {busy ? 'Working…' : 'New password'}</button>
              <button onClick={revoke} disabled={busy} className="text-sm text-red-700 hover:underline inline-flex items-center gap-1 px-2"><ShieldOff size={13} /> Revoke</button>
            </>}
          </div>
          {active && <p className="text-[11px] text-gray-400 mt-2">New password and revoke refuse new logins right away; a connection already open keeps working until it drops. Revoke also removes its write permission at once.</p>}
        </>
      )}
    </div>
  );
}
